import React from "react"
import { Box, TableRow, TableCell, Checkbox, IconButton } from "@mui/material"
import { makeStyles } from "makeStyles"
import Icon from "@mdi/react"
import { mdiDelete, mdiCartPlus, mdiChevronUp, mdiChevronRight } from "@mdi/js"

import Button from "components/Buttons/Button"
import ChangeAmountButton from "components/Buttons/ChangeAmountButton"
import Price from "components/Price"

import useToggle from "@secureo/common/hooks/useToggle"

import { getConfigurationName } from "containers/CartPage/LineItem"
import getDeliveryTimeWorkDaysString from "@secureo/common/utils/commerceTools/formatters/getDeliveryTimeWorkDaysString"

import { MergedConfiguredLineItem } from "@secureo/common/typings/Cart"
import { CountryCode } from "@secureo/common/typings/CountryCode"

const useStyles = makeStyles()({
	addToCartButton: {
		background: "#4691CE",
		border: "1px solid #4691CE",
		boxShadow: "none",
		color: "white",
		height: 40,
		width: 40,
		padding: 2,
		"&:hover": {
			background: "none",
			border: "1px solid #4691CE",
			color: "#4691CE",
		},
	},
	deleteButton: {
		background: "none",
		border: "1px solid #C3423F",
		boxShadow: "none",
		color: "#C3423F",
		height: 40,
		width: 40,
		padding: 2,
		"&:hover": {
			background: "#C3423F",
			color: "white",
		},
	},
	buttonGrid: {
		display: "flex",
		alignItems: "center",
		float: "right",
	},
	buttonText: {
		display: "block",
		"@media only screen and (max-width: 960px)": {
			display: "none",
		},
	},
})

const ShoppingListLineItem = ({
	lineItem,
	countryCode,
	isSelected,
	toggleIsSelected,
	isLoading,
	addToCart,
	changeQuantity,
	remove,
}: Props) => {
	const { classes } = useStyles()
	const [showConfigurations, toggleShowConfigurations] = useToggle(false)
	const {
		name,
		variant: { sku, images = [], deliveryTimeWorkDaysByShopCountryCode },
		taxedPrice: {
			totalGross: { centAmount: totalGrossCents },
		},
		configurations = [],
		quantity: lineItemQuantity,
	} = lineItem
	const img = images[0] || "https://via.placeholder.com/100x100"
	const deliveryTimeWorkDays = deliveryTimeWorkDaysByShopCountryCode?.[countryCode] ?? 0
	const hasConfigurations = (configurations?.length ?? 0) > 0

	return (
		<TableRow
			hover
			role="checkbox"
			aria-checked={isSelected}
			tabIndex={-1}
			key={lineItem.id}
			selected={isSelected}
		>
			<TableCell padding="checkbox">
				<Checkbox
					checked={isSelected}
					onClick={() => toggleIsSelected(lineItem.id)}
					inputProps={{
						"aria-labelledby": lineItem.id,
					}}
				/>
			</TableCell>
			<TableCell component="th" id={lineItem.id} scope="row" padding="none">
				<img
					src={img}
					alt={name}
					style={{
						width: "100%",
						maxWidth: 100,
						maxHeight: 80,
						objectFit: "contain",
					}}
				/>
			</TableCell>

			<TableCell align="left">
				<strong>{name}</strong>
				<Box>Artikel-Nr.: {sku}</Box>
				<Box
					mb={1}
					style={{
						color: "#5DA67A",
					}}
				>
					<strong>
						Lieferzeit: {getDeliveryTimeWorkDaysString(deliveryTimeWorkDays)}
					</strong>
				</Box>

				{hasConfigurations && (
					<div>
						<Button
							color="link-no-underline"
							onClick={toggleShowConfigurations}
							disableRipple
							style={{ paddingLeft: 0 }}
						>
							<Icon
								path={showConfigurations ? mdiChevronUp : mdiChevronRight}
								size={1}
								color="black"
							/>
							<strong style={{ whiteSpace: "nowrap", fontSize: "0.9rem" }}>
								Konfigurationen {showConfigurations ? "verbergen" : "anzeigen"}
							</strong>
						</Button>

						<table style={{ width: "100%" }} cellPadding={5}>
							<tbody>
								{showConfigurations &&
									configurations.map((configuration) => {
										const {
											id,
											name,
											quantity,
											taxedPrice: {
												totalGross: { centAmount: totalGrossCents },
											},
										} = configuration

										const configurationName = getConfigurationName(name)

										return (
											<tr
												key={id}
												style={{
													borderBottom: "1px solid whitesmoke",
													fontWeight: 300,
												}}
											>
												<td style={{ width: 30 }}>
													{quantity / lineItemQuantity}x
												</td>
												<td
													style={{
														textAlign: "left",
														whiteSpace: "nowrap",
													}}
												>
													{configurationName}
												</td>
												<td style={{ textAlign: "right" }}>
													<Price priceInEuroCents={totalGrossCents} />
												</td>
											</tr>
										)
									})}
							</tbody>
						</table>
					</div>
				)}
			</TableCell>

			<TableCell align="right" style={{ verticalAlign: "top" }}>
				<Box mb={2} style={{ fontSize: 18 }}>
					<strong>
						<Price priceInEuroCents={totalGrossCents} />
					</strong>
				</Box>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-end",
					}}
				>
					<IconButton
						color="primary"
						aria-label="remove lineItem from cart"
						onClick={() => {
							remove(lineItem)
						}}
						disabled={isLoading}
						size="small"
						className={classes.deleteButton}
					>
						<Icon path={mdiDelete} size={1} />
					</IconButton>
					<Box mx={1}>
						<ChangeAmountButton
							quantity={lineItemQuantity}
							onChange={(newConfiguredLineItemQuantity) =>
								changeQuantity(lineItem, newConfiguredLineItemQuantity)
							}
							minQuantity={1}
							disabled={isLoading}
						/>
					</Box>
					{addToCart && (
						<IconButton
							color="secondary"
							aria-label="add"
							size="small"
							className={classes.addToCartButton}
							onClick={() => {
								addToCart(lineItem)
							}}
							disabled={isLoading}
						>
							<Icon path={mdiCartPlus} size={1} />
						</IconButton>
					)}
				</div>
			</TableCell>
		</TableRow>
	)
}

interface Props {
	lineItem: MergedConfiguredLineItem
	countryCode: CountryCode
	isSelected: boolean
	toggleIsSelected: (lineItemId: string) => void
	isLoading: boolean
	addToCart
	changeQuantity
	remove
}

export default ShoppingListLineItem
